/**
 * Market facet rollup: country leaves → region/continent parents.
 * Filtering "Europe" must match documents tagged Germany (library-search-spec AC13).
 *
 * Keys are normalized lowercase for matching. Values include common aliases
 * (ISO codes, names, EU) so both auto-extracted and human-corrected values work.
 */

/**
 * Per-country alias groups (display name variants + ISO-style codes) for the
 * four "leaf-complete" regions below (`europe`, `north america`, `apac`,
 * `latam`). Each inner array is exactly the run of aliases that were already
 * adjacent in this file's flat leaf lists for one country — grouping only
 * makes that existing adjacency explicit; no country or alias is added.
 *
 * This is what lets `marketMatchesFilter` resolve a country's code and its
 * display name to the same key (F1: a document tagged "US" must match a
 * filter chip "United States", and vice versa) without importing the
 * backend's ~200-entry `COUNTRY_MAP` (a different package layer) or
 * hand-duplicating a second country list here.
 *
 * `western europe` and `eu` below stay flat, untouched — they are partial
 * subsets of `europe` kept for region rollup only, not alias resolution.
 */
const EUROPE_COUNTRY_GROUPS: Array<Array<string>> = [
  ['albania', 'al'],
  ['andorra', 'ad'],
  ['austria', 'at'],
  ['belarus', 'by'],
  ['belgium', 'be'],
  ['bosnia', 'bosnia and herzegovina', 'ba'],
  ['bulgaria', 'bg'],
  ['croatia', 'hr'],
  ['cyprus', 'cy'],
  ['czech', 'czech republic', 'czechia', 'cz'],
  ['denmark', 'dk'],
  ['estonia', 'ee'],
  ['finland', 'fi'],
  ['france', 'fr'],
  ['germany', 'de'],
  ['greece', 'gr'],
  ['hungary', 'hu'],
  ['iceland', 'is'],
  ['ireland', 'ie'],
  ['italy', 'it'],
  ['latvia', 'lv'],
  ['lithuania', 'lt'],
  ['luxembourg', 'lu'],
  ['malta', 'mt'],
  ['moldova', 'md'],
  ['monaco', 'mc'],
  ['montenegro', 'me'],
  ['netherlands', 'holland', 'nl'],
  ['north macedonia', 'macedonia', 'mk'],
  ['norway', 'no'],
  ['poland', 'pl'],
  ['portugal', 'pt'],
  ['romania', 'ro'],
  ['russia', 'russian federation', 'ru'],
  ['serbia', 'rs'],
  ['slovakia', 'sk'],
  ['slovenia', 'si'],
  ['spain', 'es'],
  ['sweden', 'se'],
  ['switzerland', 'ch'],
  ['ukraine', 'ua'],
  [
    'united kingdom',
    'uk',
    'gb',
    'britain',
    'great britain',
    'england',
    'scotland',
    'wales',
    'northern ireland',
  ],
]

const NORTH_AMERICA_COUNTRY_GROUPS: Array<Array<string>> = [
  ['united states', 'usa', 'us', 'u.s.', 'u.s.a.', 'america'],
  ['canada', 'ca'],
  ['mexico', 'mx'],
]

const APAC_COUNTRY_GROUPS: Array<Array<string>> = [
  ['australia', 'au'],
  ['china', 'cn'],
  ['hong kong', 'hk'],
  ['india', 'in'],
  ['indonesia', 'id'],
  ['japan', 'jp'],
  ['malaysia', 'my'],
  ['new zealand', 'nz'],
  ['philippines', 'ph'],
  ['singapore', 'sg'],
  ['south korea', 'korea', 'kr'],
  ['taiwan', 'tw'],
  ['thailand', 'th'],
  ['vietnam', 'vn'],
]

const LATAM_COUNTRY_GROUPS: Array<Array<string>> = [
  ['argentina', 'ar'],
  ['brazil', 'br'],
  ['chile', 'cl'],
  ['colombia', 'co'],
  ['mexico', 'mx'],
  ['peru', 'pe'],
]

export const MARKET_REGION_CHILDREN: Record<string, Array<string>> = {
  europe: EUROPE_COUNTRY_GROUPS.flat(),
  'western europe': [
    'austria',
    'at',
    'belgium',
    'be',
    'france',
    'fr',
    'germany',
    'de',
    'ireland',
    'ie',
    'luxembourg',
    'lu',
    'netherlands',
    'holland',
    'nl',
    'switzerland',
    'ch',
    'united kingdom',
    'uk',
    'gb',
    'britain',
  ],
  eu: [
    'austria',
    'at',
    'belgium',
    'be',
    'bulgaria',
    'bg',
    'croatia',
    'hr',
    'cyprus',
    'cy',
    'czech',
    'czech republic',
    'czechia',
    'cz',
    'denmark',
    'dk',
    'estonia',
    'ee',
    'finland',
    'fi',
    'france',
    'fr',
    'germany',
    'de',
    'greece',
    'gr',
    'hungary',
    'hu',
    'ireland',
    'ie',
    'italy',
    'it',
    'latvia',
    'lv',
    'lithuania',
    'lt',
    'luxembourg',
    'lu',
    'malta',
    'mt',
    'netherlands',
    'holland',
    'nl',
    'poland',
    'pl',
    'portugal',
    'pt',
    'romania',
    'ro',
    'slovakia',
    'sk',
    'slovenia',
    'si',
    'spain',
    'es',
    'sweden',
    'se',
  ],
  'north america': NORTH_AMERICA_COUNTRY_GROUPS.flat(),
  apac: APAC_COUNTRY_GROUPS.flat(),
  latam: LATAM_COUNTRY_GROUPS.flat(),
}

/**
 * Alias → canonical-key lookup built from the same country groups above.
 * Two labels canonicalize to the same key iff they are aliases (name and/or
 * ISO-style code) of the SAME country — this is what lets `marketMatchesFilter`
 * treat "US" and "United States" as the same market (F1) while still keeping
 * two different countries (e.g. "France" vs "Germany") distinct, since they
 * never share a canonical key.
 */
const COUNTRY_ALIAS_TO_CANONICAL: Map<string, string> = (() => {
  const map = new Map<string, string>()
  const groups = [
    ...EUROPE_COUNTRY_GROUPS,
    ...NORTH_AMERICA_COUNTRY_GROUPS,
    ...APAC_COUNTRY_GROUPS,
    ...LATAM_COUNTRY_GROUPS,
  ]
  for (const group of groups) {
    const canonicalKey = group[0]
    for (const alias of group) {
      map.set(alias, canonicalKey)
    }
  }
  return map
})()

function canonicalCountryKey(normalizedLabel: string): string {
  return COUNTRY_ALIAS_TO_CANONICAL.get(normalizedLabel) ?? normalizedLabel
}

export function normalizeMarketLabel(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ')
}

/**
 * True when `documentMarket` matches `filterMarket` exactly or via region rollup.
 * e.g. filter Europe + document Germany → true.
 */
export function marketMatchesFilter(
  documentMarket: string | null | undefined,
  filterMarket: string | null | undefined,
): boolean {
  if (!filterMarket) return true
  if (!documentMarket) return false
  const filter = normalizeMarketLabel(filterMarket)
  const actual = normalizeMarketLabel(documentMarket)
  if (actual === filter) return true

  // A country's ISO-style code and its display name are the same market
  // (F1): canonicalize both sides against the shared country alias groups
  // before falling through to region rollup. Two DIFFERENT countries never
  // share a canonical key, so this cannot introduce a leaf<->leaf false
  // match (see market-hierarchy.spec.ts).
  if (canonicalCountryKey(filter) === canonicalCountryKey(actual)) {
    return true
  }

  const children = MARKET_REGION_CHILDREN[filter]
  if (children && children.includes(actual)) {
    return true
  }

  // Also allow filtering by a leaf that is an alias of the same country.
  for (const [, leaves] of Object.entries(MARKET_REGION_CHILDREN)) {
    if (leaves.includes(filter) && leaves.includes(actual)) {
      // Same region membership alone is not enough for leaf↔leaf
      // (Germany should not match France). Only exact or parent→child.
      break
    }
  }

  return false
}

export function expandMarketFilterValues(filterMarket: string): Array<string> {
  const filter = normalizeMarketLabel(filterMarket)
  const children = MARKET_REGION_CHILDREN[filter]
  if (!children) {
    return [filterMarket.trim()]
  }
  return [filterMarket.trim(), ...children]
}
